// vendor_display.js — print-preview vendor resolver
//
// Shared resolver for rendering vendor details in the print surface.
// Ensures consistent handling across Current Services, Unmet Needs,
// Goal Resources, and goal-library vendors. Uses vendor_library's
// resolveVendorDisplay internally, adding print-specific fallback
// logic for removed vendors.
//
// Three branches:
//   1. Linked: vendorId exists AND library record found → library values win
//   2. Unlinked (legacy): no vendorId → use entry's own fields directly
//   3. Orphan: vendorId exists BUT library record missing → legacy fields,
//      with "(vendor removed)" fallback when name is blank

import { resolveVendorDisplay, vendorGet } from "vendor_library"

/**
 * Resolve vendor display data for print output.
 *
 * @param {Object} entry - The resource/service entry (may have vendorId + legacy fields)
 * @param {Function} [vendorGetFn=vendorGet] - Vendor lookup function (injectable for tests)
 * @returns {Object} { name, phone, email, website, address, linked, unlinked }
 *   - linked: true when library data was used
 *   - unlinked: true when vendorId was present but library record was not found
 *   - name: never blank when unlinked — falls back to "(vendor removed)"
 */
export function resolvePrintVendor(entry, vendorGetFn = vendorGet) {
  const result = resolveVendorDisplay(entry, vendorGetFn)

  // Orphan case: vendorId was set but library record is missing.
  // If no legacy name exists, show "(vendor removed)" so the print
  // never has a blank vendor slot.
  if (result.unlinked && !result.name) {
    return {
      ...result,
      name: "(vendor removed)"
    }
  }

  return result
}

/**
 * Resolve a single vendor object from a resource's vendors array.
 * Handles both linked vendors (with vendorId) and legacy inline vendor objects.
 *
 * @param {Object} vendorEntry - Single vendor from r.vendors array
 * @param {Function} [vendorGetFn=vendorGet] - Vendor lookup function (injectable for tests)
 * @returns {Object} { name, phone, email, website, address, linked, unlinked }
 */
export function resolvePrintVendorEntry(vendorEntry, vendorGetFn = vendorGet) {
  if (!vendorEntry || typeof vendorEntry !== "object") {
    return { name: "", phone: "", email: "", website: "", address: "", linked: false, unlinked: false }
  }
  return resolvePrintVendor(vendorEntry, vendorGetFn)
}
